import { displayText } from "../../../../locales/display";
import { useLocale } from "../../../../hooks/useLocale";
import { msg } from "../../../../locales/translate";
import { Box, HBox, PanelBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Input from "@ui/input/Input";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { ChevronDown, ChevronUp, MailPlus } from "lucide-react";
import { useState } from "react";
import { TFeaturedComponent } from "src/Featuresmap";
import { GD } from "src/GD";
import { getRoleName } from "src/permissions";
import { useHasRole } from "src/hooks/tools";
import { IClient } from "src/services/ClientService";
import { notify } from "../../../../tools/notify";
import { NoRecords } from "../../../providers/NoRecords";
import { S_CONFIRM } from "../../../popup/general/ConfirmDialog";

export interface IClientPageUsersProps {
	client: IClient;
}

const MAX_INITIAL_PARTS = 2;
const MAX_INITIAL_LENGTH = 2;

const normalizeState = (value?: string | number | null) => String(value ?? "").trim().toLowerCase();

const toTitle = (value?: string | number | null, fallback = msg("notAvailable")) => {
	const text = String(value ?? "").trim();
	if (!text) return fallback;
	return displayText(text
		.replace(/[_-]+/g, " ")
		.split(" ")
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" "));
};

const getUserInitials = (name?: string | null, email?: string | null) => {
	const source = name?.trim() || email?.trim() || "User";
	const parts = source.split(/[\s@._-]+/).filter(Boolean);
	return (
		parts
			.slice(0, MAX_INITIAL_PARTS)
			.map(part => part.charAt(0).toUpperCase())
			.join("")
			.slice(0, MAX_INITIAL_LENGTH) || "US"
	);
};

const getUserDisplayName = (user: IClientUser) => user.name?.trim() || user.email?.split("@")[0] || msg("templateUser_039247f9", { v0: user.id });

const getRoleLabel = (roleID?: number | string | null) => {
	if (roleID === null || roleID === undefined || roleID === "") return msg("notAssigned");
	const role = getRoleName({ id: roleID });
	return role ? toTitle(role) : msg("templateRole_5cd184f9", { v0: roleID });
};

const getEmailStatusLabel = (value?: string | null) => {
	const status = normalizeState(value);
	if (status === "approved" || status === "verified") return displayText("Verified");
	if (status === "not-approved" || status === "not_approved" || status === "unverified") return msg("notVerified");
	return toTitle(value);
};

const getTwoFactorLabel = (value?: string | null) => {
	const status = normalizeState(value);
	if (status === "activated" || status === "active" || status === "enabled") return msg("enabled");
	if (status === "disabled" || status === "inactive") return msg("notEnabled");
	return toTitle(value);
};

const canResendInvite = (user: IClientUser) => !["approved", "verified"].includes(normalizeState(user.status_email));

const getStatusColors = (value?: string | null) => {
	const status = normalizeState(value);
	if (["active", "approved", "activated", "enabled", "verified"].includes(status)) return { backgroundColor: "#E8F7EF", color: "#147A45" };
	if (["blocked", "suspended"].includes(status)) return { backgroundColor: "#FCECEC", color: "#B42318" };
	if (["disabled", "deleted", "inactive"].includes(status)) return { backgroundColor: "#F1F5F9", color: "#64748B" };
	return { backgroundColor: "#FFF7E8", color: "#A15C07" };
};

const UserStatusBadge = ({ value }: { value?: string | null }) => {
	useLocale();
	const colors = getStatusColors(value);
	return (
		<Typo
			sx={{
				display: "inline-flex",
				alignItems: "center",
				width: "max-content",
				padding: "3px 8px",
				borderRadius: "4px",
				backgroundColor: colors.backgroundColor,
				color: colors.color,
				fontSize: "11px",
				lineHeight: "16px",
				fontWeight: 600,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				whiteSpace: "nowrap",
			}}>
			{toTitle(value, msg("unknown"))}
		</Typo>
	);
};

const SummaryField = ({ label, children }: { label: string; children: React.ReactNode }) => { useLocale(); return ((
	<VBox gap="4px" sx={{ minWidth: "100px", alignItems: "flex-start" }}>
		<Typo sx={{ color: "#8B8580", fontSize: "10px", lineHeight: "13px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
			{label}
		</Typo>
		{children}
	</VBox>
)); };

const DetailField = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => { useLocale(); return ((
	<VBox gap="4px" sx={{ minWidth: 0 }}>
		<Typo sx={{ color: "#8B8580", fontSize: "11px", lineHeight: "14px", fontWeight: 600 }}>{label}</Typo>
		<Typo
			sx={{
				color: "#2C2926",
				fontSize: "13px",
				lineHeight: "18px",
				fontWeight: 500,
				fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : undefined,
				wordBreak: "break-word",
			}}>
			{value}
		</Typo>
	</VBox>
)); };

export const ClientPageUsers: TFeaturedComponent<IClientPageUsersProps> = ({ client }: IClientPageUsersProps) => {
	useLocale();
	const canManageUsers = useHasRole("admin");
	const [response, reloadUsers, , busy] = GD.REQ_CLIENT_USERS.useRequest(undefined, { clientID: client.id });
	const [expandedUserIds, setExpandedUserIds] = useState<number[]>([]);
	const [showInviteForm, setShowInviteForm] = useState(false);
	const [inviteBusy, setInviteBusy] = useState(false);
	const [inviteError, setInviteError] = useState<IError>();
	const [inviteEmail, setInviteEmail] = useState({ value: "" });
	const [inviteName, setInviteName] = useState({ value: "" });
	const [actionBusy, setActionBusy] = useState<number | null>(null);

	const users = response?.data ?? [];

	const toggleUserExpanded = (userID: number) => {
		setExpandedUserIds(current => (current.includes(userID) ? current.filter(id => id !== userID) : [...current, userID]));
	};

	const resetInviteForm = () => {
		setInviteEmail({ value: "" });
		setInviteName({ value: "" });
		setInviteError(undefined);
		setInviteBusy(false);
		setShowInviteForm(false);
	};

	const onInviteUser = async () => {
		if (!canManageUsers) return;
		setInviteError(undefined);
		const email = inviteEmail.value?.trim();

		if (!email) {
			setInviteError({ code: -1, message: msg("emailAddressIsRequired") });
			return;
		}
		if (!/^\S+@\S+\.\S+$/.test(email)) {
			setInviteError({ code: -1, message: msg("enterAValidEmailAddress") });
			return;
		}

		setInviteBusy(true);
		const inviteResponse = await GD.REQ_CLIENT_USER_CREATE.request({
			clientID: client.id,
			email,
			name: inviteName.value?.trim() || undefined,
		});
		setInviteBusy(false);

		if (inviteResponse.error) {
			setInviteError(inviteResponse.error);
			return;
		}

		notify.info(msg("invitationSent"));
		await reloadUsers({ clientID: client.id });
		resetInviteForm();
	};

	const runUserAction = async (userID: number, request: Promise<IAPIResponse>, successMessage: string, errorMessage: string) => {
		setActionBusy(userID);
		const actionResponse = await request;
		setActionBusy(null);

		if (actionResponse.error) {
			notify.error(actionResponse.error.message || errorMessage);
			return;
		}

		notify.info(successMessage);
		await reloadUsers({ clientID: client.id });
	};

	const onBlockUser = (user: IClientUser) => {
		if (!canManageUsers) return;
		S_CONFIRM.invoke({
			title: msg("blockClientAccess"),
			message: msg("templateBlockFromAccessingTheUserAccountWillRemainAvailableInOth_12c1d09a", { v0: getUserDisplayName(user), v1: client.name }),
			confirm: msg("blockAccess"),
			reject: "Cancel",
			onResult: result => {
				if (result !== "confirm") return;
				void runUserAction(
					user.id,
					GD.REQ_CLIENT_USER_BLOCK.request({ clientID: client.id, userID: user.id }),
					msg("clientAccessBlocked"),
					msg("unableToBlockClientAccess"),
				);
			},
		});
	};

	const onUnblockUser = (user: IClientUser) => {
		if (!canManageUsers) return;
		void runUserAction(
			user.id,
			GD.REQ_CLIENT_USER_UNBLOCK.request({ clientID: client.id, userID: user.id }),
			msg("clientAccessRestored"),
			msg("unableToRestoreClientAccess"),
		);
	};

	const onRemoveUser = (user: IClientUser) => {
		if (!canManageUsers) return;
		S_CONFIRM.invoke({
			title: msg("removeFromClient"),
			message: msg("templateRemoveFromThisRemovesAccessToThisClientButDoesNotDeleteT_0612ef82", { v0: getUserDisplayName(user), v1: client.name }),
			confirm: msg("remove"),
			reject: "Cancel",
			onResult: result => {
				if (result !== "confirm") return;
				void runUserAction(
					user.id,
					GD.REQ_CLIENT_USER_REMOVE.request({ clientID: client.id, userID: user.id }),
					msg("userRemovedFromClient"),
					msg("unableToRemoveUserFromClient"),
				);
			},
		});
	};

	const onResendInvite = (user: IClientUser) => {
		if (!canManageUsers) return;
		void runUserAction(
			user.id,
			GD.REQ_CLIENT_USER_CREATE.request({ clientID: client.id, email: user.email, name: user.name || undefined }),
			msg("invitationResent"),
			msg("unableToResendInvitation"),
		);
	};

	return (
		<VBox gap="12px">
			<ProgressBar busy={busy || inviteBusy || actionBusy !== null} />

			<HBox gap="16px" sx={{ width: "100%", alignItems: "flex-end", flexWrap: "wrap" }}>
				<VBox gap="3px">
					<Typo sx={{ color: "#2C2926", fontSize: "15px", lineHeight: "20px", fontWeight: 600 }}>{msg("clientUsers")}</Typo>
					<Typo sx={{ color: "#7B746E", fontSize: "12px", lineHeight: "17px" }}>
						{busy && users.length === 0
							? msg("loadingPeopleWithAccess")
							: msg("clientAccessPeople", { count: users.length })}
					</Typo>
				</VBox>
				<Spacer grow />
				{canManageUsers && <Button title={msg("inviteUser")} variant="primarySmall" icon={<MailPlus size={15} />} onClick={() => setShowInviteForm(true)} />}
			</HBox>

			<Error error={response?.error} />

			{!response?.error && !busy && users.length === 0 && (
				<NoRecords
					line1={msg("noUsersHaveAccessToThisClient")}
					line2={msg("inviteAUserToGiveThemAccess")}
					title={msg("inviteUser")}
					onClick={() => setShowInviteForm(true)}
				/>
			)}

			<VBox spacer="tiny">
				{users.map(user => {
					const rowBusy = actionBusy === user.id;
					const accessStatus = user.status_client ?? user.status;
					const isBlocked = normalizeState(accessStatus) === "blocked";
					const isExpanded = expandedUserIds.includes(user.id);

					return (
						<PanelBox
							key={user.id}
							variant="strict"
							sx={{
								padding: "12px 16px",
								borderRadius: "6px",
								border: "1px solid #EEE8E4",
								boxShadow: "0 1px 2px rgba(59, 43, 32, 0.04)",
								gap: "12px",
							}}>
							<HBox gap="18px" sx={{ width: "100%", alignItems: "center", flexWrap: "wrap" }}>
								<HBox gap="11px" sx={{ flex: "1 1 260px", minWidth: 0, alignItems: "center" }}>
									<Box
										sx={{
											width: "36px",
											height: "36px",
											minWidth: "36px",
											borderRadius: "8px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: "#F8F0ED",
											border: "1px solid #EADCD5",
											color: "#9F3F22",
											fontSize: "12px",
											fontWeight: 700,
										}}>
										{getUserInitials(user.name, user.email)}
									</Box>
									<VBox gap="1px" sx={{ minWidth: 0 }}>
										<Typo sx={{ color: "#1F1D1B", fontSize: "13px", lineHeight: "18px", fontWeight: 600 }}>{getUserDisplayName(user)}</Typo>
										<Typo sx={{ color: "#7B746E", fontSize: "11px", lineHeight: "16px", wordBreak: "break-word" }}>{user.email}</Typo>
									</VBox>
								</HBox>

								<SummaryField label={msg("clientAccess")}>
									<UserStatusBadge value={accessStatus} />
								</SummaryField>
								<SummaryField label={msg("role")}>
									<Typo sx={{ color: "#4A4642", fontSize: "12px", lineHeight: "17px", fontWeight: 500 }}>{getRoleLabel(user.role_id)}</Typo>
								</SummaryField>
								<SummaryField label={msg("email_969ccbd3")}>
									<Typo sx={{ color: "#4A4642", fontSize: "12px", lineHeight: "17px", fontWeight: 500 }}>{getEmailStatusLabel(user.status_email)}</Typo>
								</SummaryField>
								<SummaryField label="2FA">
									<Typo sx={{ color: "#4A4642", fontSize: "12px", lineHeight: "17px", fontWeight: 500 }}>{getTwoFactorLabel(user.status_2fa)}</Typo>
								</SummaryField>

								<HBox gap="8px" sx={{ marginLeft: "auto", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
									{canManageUsers && canResendInvite(user) && (
										<Button
											title={rowBusy ? msg("sending") : msg("resendInvitation")}
											variant="secondarySmall"
											disabled={busy || inviteBusy || rowBusy}
											onClick={() => onResendInvite(user)}
										/>
									)}
									<Button
										title={isExpanded ? msg("hideDetails") : msg("details")}
										variant="secondarySmall"
										iconRight={isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
										onClick={() => toggleUserExpanded(user.id)}
									/>
								</HBox>
							</HBox>

							{isExpanded && (
								<VBox gap="14px" sx={{ borderTop: "1px solid #EEE8E4", paddingTop: "14px" }}>
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
											gap: "14px 24px",
										}}>
										<DetailField label={msg("clientAccessStatus")} value={toTitle(accessStatus)} />
										<DetailField label={msg("accountStatus")} value={toTitle(user.status)} />
										<DetailField label={msg("accountType")} value={toTitle(user.type)} />
										<DetailField label={msg("role")} value={getRoleLabel(user.role_id)} />
										<DetailField label={msg("emailVerification")} value={getEmailStatusLabel(user.status_email)} />
										<DetailField label={msg("twoFactorAuthentication")} value={getTwoFactorLabel(user.status_2fa)} />
										<DetailField label={msg("userId")} value={String(user.id)} mono />
									</Box>

									{canManageUsers && <HBox gap="8px" sx={{ alignItems: "center", flexWrap: "wrap", paddingTop: "12px", borderTop: "1px solid #F2EEEB" }}>
										<Typo sx={{ color: "#7B746E", fontSize: "11px", fontWeight: 600 }}>{msg("accessActions")}</Typo>
										<Spacer grow />
										{isBlocked ? (
											<Button
												title={rowBusy ? msg("restoring") : msg("restoreAccess")}
												variant="secondarySmall"
												disabled={busy || inviteBusy || rowBusy}
												onClick={() => onUnblockUser(user)}
											/>
										) : (
											<Button
												title={rowBusy ? msg("blocking") : msg("blockAccess")}
												variant="secondarySmall"
												disabled={busy || inviteBusy || rowBusy}
												onClick={() => onBlockUser(user)}
											/>
										)}
										<Button
											title={rowBusy ? msg("removing_60d18e42") : msg("removeFromClient")}
											variant="secondarySmall"
											sx={{ color: "#B42318", border: "1px solid #F0C8C0", "&:hover": { backgroundColor: "#FFF1EE" } }}
											disabled={busy || inviteBusy || rowBusy}
											onClick={() => onRemoveUser(user)}
										/>
									</HBox>}
								</VBox>
							)}
						</PanelBox>
					);
				})}
			</VBox>

			{canManageUsers && showInviteForm && (
				<Panel
					busy={inviteBusy}
					contentPadding="20px"
					sx={{ width: "90vw", maxWidth: "500px", padding: "0", maxHeight: "90vh", backgroundColor: "#FFFFFF" }}
					decorator="absolute"
					label={msg("inviteClientUser")}
					growContent
					onClose={resetInviteForm}>
					<VBox gap="16px">
						<Typo sx={{ color: "#7B746E", fontSize: "13px", lineHeight: "19px" }}>{msg("theUserWillReceiveAnEmailInvitationToAccess")}{client.name}.
						</Typo>
						<VBox gap="10px">
							<Input label={msg("emailAddress_f2488fd4")} data={inviteEmail} name="value" onChange={() => setInviteError(undefined)} />
							<Input label={msg("nameOptional")} data={inviteName} name="value" onChange={() => setInviteError(undefined)} />
							<Error error={inviteError} />
						</VBox>
						<HBox gap="10px" sx={{ width: "100%", paddingTop: "4px" }}>
							<Spacer grow />
							<Button title={msg("cancel")} variant="secondary" disabled={inviteBusy} onClick={resetInviteForm} />
							<Button title={inviteBusy ? msg("sending") : msg("sendInvitation")} variant="primary" disabled={inviteBusy} onClick={onInviteUser} />
						</HBox>
					</VBox>
				</Panel>
			)}
		</VBox>
	);
};

ClientPageUsers.feature = "client_users";
